import React, { useState, useEffect, useRef } from 'react';
import classes from './TopMenu.module.css';
import HamburgerMenu from './HamburgerMenu';
import FestivalInfo from '../Modals/FestivalInfo';

const MenuIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M4 6H20M4 12H20M4 18H20" stroke="#2c3030" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

// "17–20 Nov 2026" when same month/year, else "28 Dec 2025 – 2 Jan 2026";
// falls back to "From 17 Nov 2026" when there's no end date.
const formatFestivalDates = (startdate, enddate) => {
  if (!startdate) return null;
  const start = new Date(startdate);
  if (isNaN(start)) return null;
  const startDay = start.getDate();
  const startMonth = start.toLocaleString(undefined, { month: 'short' });
  const startYear = start.getFullYear();

  if (!enddate) return `From ${startDay} ${startMonth} ${startYear}`;

  const end = new Date(enddate);
  if (isNaN(end)) return `From ${startDay} ${startMonth} ${startYear}`;

  const endDay = end.getDate();
  const endMonth = end.toLocaleString(undefined, { month: 'short' });
  const endYear = end.getFullYear();

  if (startMonth === endMonth && startYear === endYear) {
    return `${startDay}–${endDay} ${endMonth} ${endYear}`;
  }
  return `${startDay} ${startMonth} ${startYear} – ${endDay} ${endMonth} ${endYear}`;
};

const TopMenu = ({ group, festivalInfo, setCurrentScreen, currentScreen }) => {
  const [menuVisible, setMenuVisible] = useState(false);
  const [festivalInfoVisible, setFestivalInfoVisible] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const lastScrollTopRef = useRef(0);

  // The header collapses to a slim logo+hamburger bar on scroll, like a
  // native iOS nav bar — but only on the checklist screens (My Gear/Need/
  // Pack). Home is the app's hub screen and stays fixed per spec, so it's
  // excluded here even though it shares this same TopMenu instance and
  // Root.js's single `.content` scroll container.
  const canCollapse = currentScreen !== 'home';

  useEffect(() => {
    const contentEl = document.querySelector('.content');
    if (!contentEl || !canCollapse) {
      setCollapsed(false);
      return undefined;
    }

    // Ignore scroll near the very top (rubber-band bounce) so the header
    // doesn't flicker collapsed/expanded on tiny jitters.
    const threshold = 12;
    lastScrollTopRef.current = contentEl.scrollTop;
    setCollapsed(contentEl.scrollTop > threshold);

    const handleScroll = () => {
      const scrollTop = contentEl.scrollTop;
      const delta = scrollTop - lastScrollTopRef.current;
      if (scrollTop <= threshold) {
        setCollapsed(false);
      } else if (delta > 4) {
        setCollapsed(true);
      } else if (delta < -4) {
        setCollapsed(false);
      }
      lastScrollTopRef.current = scrollTop;
    };

    contentEl.addEventListener('scroll', handleScroll, { passive: true });
    return () => contentEl.removeEventListener('scroll', handleScroll);
  }, [canCollapse, currentScreen]);

  // group.festival is a snapshot copied onto the group once, when the event
  // was added — it never updates again, so it goes stale the moment an
  // admin edits that festival's name/location/dates/image afterwards.
  // festivalInfo is the live festivals/{id} doc (see Root.js's onSnapshot
  // listener) — prefer it, falling back to group.festival only for a
  // custom event (no catalog festival, so no live doc to listen to) or
  // before the live listener's first snapshot has arrived.
  const festival = festivalInfo || group?.festival;
  const festivalName = festival?.name || group?.name || 'Camping Buddy';
  const festivalLocation = festival?.location
    ? `${festival.location}${festival.country ? ', ' + festival.country : ''}`
    : null;
  const festivalDates = formatFestivalDates(festival?.startdate, festival?.enddate);
  const festivalImage = festival?.image?.src || null;

  return (
    <>
      <header className={`${classes.topMenu} ${collapsed ? classes.collapsed : ''}`}>

        {/* Festival logo + name */}
        <button
          className={classes.festivalInfo}
          onClick={() => setFestivalInfoVisible(!festivalInfoVisible)}
          aria-label="Festival info"
        >
          {/* Logo circle */}
          <div className={classes.logoCircle}>
            {festivalImage ? (
              <img
                src={festivalImage}
                alt={festivalName}
                className={classes.logoImage}
              />
            ) : (
              <span className={classes.logoFallback}>
                {festivalName.charAt(0)}
              </span>
            )}
          </div>

          {/* Name + location */}
          <div className={classes.festivalText}>
            <div className={classes.festivalName}>{festivalName}</div>
            {(festivalLocation || festivalDates) && (
              <div className={classes.festivalSubtitleRow}>
                {festivalLocation && (
                  <span className={`${classes.festivalLocation} ${classes.festivalLocationText}`}>
                    {festivalLocation}
                  </span>
                )}
                {festivalDates && (
                  <span className={`${classes.festivalLocation} ${classes.festivalDateText}`}>
                    {festivalLocation ? ' · ' : ''}{festivalDates}
                  </span>
                )}
              </div>
            )}
          </div>
        </button>

        {/* Hamburger button */}
        <button
          className={classes.menuButton}
          onClick={() => setMenuVisible(!menuVisible)}
          aria-label="Open menu"
        >
          <MenuIcon />
        </button>

      </header>

      <HamburgerMenu
        visible={menuVisible}
        setMenuVisible={setMenuVisible}
        group={group}
        setCurrentScreen={setCurrentScreen}
      />

      {festivalInfoVisible && (
        <FestivalInfo
          id={festival?.id}
          onConfirm={() => setFestivalInfoVisible(false)}
          onCancel={() => setFestivalInfoVisible(false)}
        />
      )}
    </>
  );
};

export default TopMenu;
